import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import api from '../services/api';
import AppLayout from '../components/AppLayout';
import { routes } from '../lib/routes';
import { labelTipo, labelStatus } from '../lib/protocoloLabels';
import './Dashboard.css';

function badgeClass(status) {
  if (status === 'em_analise' || status === 'em_exigencia') return 'dashboard-badge dashboard-badge--em_analise';
  if (status === 'aprovado') return 'dashboard-badge dashboard-badge--aprovado';
  if (status === 'indeferido') return 'dashboard-badge dashboard-badge--indeferido';
  return 'dashboard-badge dashboard-badge--default';
}

function IconStack() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M4 6h16M4 12h16M4 18h10" strokeLinecap="round" />
    </svg>
  );
}

function IconClock() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" strokeLinecap="round" />
    </svg>
  );
}

function IconCheck() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconX() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
    </svg>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { data: protocolos, isLoading, isError } = useQuery('protocolos', () => api.get('/protocolos').then((r) => r.data));
  const { data: user } = useQuery('me', () => api.get('/usuarios/me').then((r) => r.data));

  const stats = {
    total: protocolos?.length || 0,
    em_analise: protocolos?.filter((p) => p.status === 'em_analise' || p.status === 'em_exigencia').length || 0,
    aprovados: protocolos?.filter((p) => p.status === 'aprovado').length || 0,
    indeferidos: protocolos?.filter((p) => p.status === 'indeferido').length || 0,
  };

  return (
    <AppLayout title="Dashboard" subtitle={`Bem-vindo, ${user?.nome || ''}`}>
      <div className="dashboard-page">
        <div className="dashboard-intro">
          <p>Visão geral dos seus protocolos e indicadores de tramitação.</p>
        </div>

        <div className="dashboard-stat-grid" role="list">
          <article className="dashboard-stat-card dashboard-stat-card--total" role="listitem">
            <div className="dashboard-stat-top">
              <p className="dashboard-stat-label">Total</p>
              <span className="dashboard-stat-icon" aria-hidden>
                <IconStack />
              </span>
            </div>
            {isLoading ? <div className="dashboard-stat-skel" /> : <p className="dashboard-stat-value">{stats.total}</p>}
          </article>

          <article className="dashboard-stat-card dashboard-stat-card--analise" role="listitem">
            <div className="dashboard-stat-top">
              <p className="dashboard-stat-label">Em análise</p>
              <span className="dashboard-stat-icon" aria-hidden>
                <IconClock />
              </span>
            </div>
            {isLoading ? <div className="dashboard-stat-skel" /> : <p className="dashboard-stat-value">{stats.em_analise}</p>}
          </article>

          <article className="dashboard-stat-card dashboard-stat-card--ok" role="listitem">
            <div className="dashboard-stat-top">
              <p className="dashboard-stat-label">Aprovados</p>
              <span className="dashboard-stat-icon" aria-hidden>
                <IconCheck />
              </span>
            </div>
            {isLoading ? <div className="dashboard-stat-skel" /> : <p className="dashboard-stat-value">{stats.aprovados}</p>}
          </article>

          <article className="dashboard-stat-card dashboard-stat-card--no" role="listitem">
            <div className="dashboard-stat-top">
              <p className="dashboard-stat-label">Indeferidos</p>
              <span className="dashboard-stat-icon" aria-hidden>
                <IconX />
              </span>
            </div>
            {isLoading ? <div className="dashboard-stat-skel" /> : <p className="dashboard-stat-value">{stats.indeferidos}</p>}
          </article>
        </div>

        {isError ? (
          <div className="panel">
            <p className="sispro-empty">Não foi possível carregar os protocolos. Atualize a página ou entre novamente.</p>
          </div>
        ) : null}

        <section className="panel dashboard-section" aria-labelledby="dashboard-protocolos-heading">
          <div className="dashboard-section-head">
            <h2 id="dashboard-protocolos-heading">Protocolos recentes</h2>
            <div className="dashboard-section-actions">
              <Link to={routes.protocolos} className="sispro-btn sispro-btn--secondary sispro-btn--sm">
                Ver todos
              </Link>
              <button type="button" className="sispro-btn sispro-btn--primary sispro-btn--sm" onClick={() => navigate(routes.novoProtocolo)}>
                Novo protocolo
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="dashboard-empty-wrap dashboard-loading" role="status">
              Carregando protocolos...
            </div>
          ) : null}

          {!isLoading && (!protocolos || protocolos.length === 0) ? (
            <div className="dashboard-empty-wrap sispro-empty">
              <p>Ainda não tem protocolos neste ambiente.</p>
              <button type="button" className="sispro-btn sispro-btn--primary" onClick={() => navigate(routes.novoProtocolo)}>
                Abrir primeiro protocolo
              </button>
            </div>
          ) : null}

          {!isLoading && protocolos && protocolos.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="dashboard-table">
                <thead>
                  <tr>
                    <th scope="col">Protocolo</th>
                    <th scope="col">Tipo</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {protocolos.slice(0, 8).map((p) => (
                    <tr
                      key={p.id}
                      tabIndex={0}
                      role="button"
                      onClick={() => navigate(routes.protocolo(p.id))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(routes.protocolo(p.id));
                        }
                      }}
                      className="dashboard-table-row-click"
                    >
                      <td>
                        <span className="dashboard-numero">{p.numero || p.id}</span>
                      </td>
                      <td>
                        <span className="dashboard-tipo">{labelTipo(p.tipo)}</span>
                      </td>
                      <td>
                        <span className={badgeClass(p.status)}>{labelStatus(p.status)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      </div>
    </AppLayout>
  );
}
